import { Fragment, useEffect, useState } from "react"
import { CreateFoodCtaBox, CreateFoodWrapper } from "./styled"
import Button from "components/Buttons/Button"
import { useCreateMyFood } from "../hooks"
import {
  useResetFoodImage,
  useUploadedImagePayload,
} from "features/image/hooks"
import { useNavigate, useSearchParams } from "react-router-dom"
import PATHS from "router/paths"
import { useMealTypeQueryParams } from "features/meal/hooks"
import PopConfirm from "components/PopConfirm"
import { useAppSelector } from "app/hooks"
import { useSetPageTitle } from "libs/ga"
import EdamaFoodDetails from "components/foodDetails/EdamaFoodDetails"
import { ICreateMyFoodPayload } from "model/food/types/foodPayload.types"
import CreateMyFoodForm from "./CreateMyFoodForm"
import SearchIngredients from "./SearchIngredients"
import CreateFoodHeader from "./CreateFoodFormHeader"
import { IMyFood } from "model/food/types/myfood.types"
import useMyFoodForm from "../hooks/myFood/useMyFoodForm"
import {
  IEdamaIngredient,
  IEdamaIngredientOption,
} from "model/food/types/edama.types"
import AppStyleCtaBox from "components/Common/AppStyleCtaBox"

export enum CreateMyFoodFormSection {
  Form = "Form",
  SearchIngredients = "SearchIngredients",
  IngredientDetails = "IngredientDetails",
}

const initFood: IMyFood = {
  source: "MY_FOOD",
  id: "",
  name: "",
  portion: 1,
  ingredients: [],
  unit: "unit(s)",
  calorie: 0,
  fat: 0,
  protein: 0,
  carbohydrate: 0,
  gram: 0,
}

const CreateMyFood = () => {
  useSetPageTitle("Create Food")
  const [isPopConfirmDiscardOpen, setIsPopConfirmDiscardOpen] = useState(false)
  const navigate = useNavigate()
  const mealTypeQueryParams = useMealTypeQueryParams()
  const createMyFoodFormProps = useMyFoodForm(initFood)
  const {
    myFood,
    onAddIngredient,
    onRemoveIngredient,
    onUpdateIngredient,
    onPortionChange,
  } = createMyFoodFormProps

  const { handleCreateFood, createFoodLoading } = useCreateMyFood()

  const imagePayload = useUploadedImagePayload()
  useResetFoodImage()

  const processImageLoading = useAppSelector(
    (state) => state.image.processImageLoading,
  )

  const [searchParams] = useSearchParams()

  useEffect(() => {
    const initMealName = searchParams.get("name")
    if (initMealName) {
      createMyFoodFormProps.onNameChange(initMealName as string)
    }
  }, [])

  const onSubmitCreateFood = () => {
    if (!myFood) return

    const ingredients: IEdamaIngredient[] = myFood.ingredients.map(
      ({ uuid, ...ingredient }) => {
        return {
          ...ingredient,
        }
      },
    )

    let payload: ICreateMyFoodPayload = {
      ...myFood,
      ingredients,
      source: "MY_FOOD",
    }

    if (imagePayload) {
      payload = { ...payload, ...imagePayload, ingredients }
    }

    handleCreateFood(payload)
  }

  const [isHideHeader, setIsHideHeader] = useState(false)
  const [section, setSection] = useState<CreateMyFoodFormSection>(
    CreateMyFoodFormSection.Form,
  )

  const [ingredientDetails, setIngredientDetails] =
    useState<null | IEdamaIngredientOption>(null)

  const showSearchIngredients = () => {
    setSection(CreateMyFoodFormSection.SearchIngredients)
  }

  const backToRecipes = () => {
    navigate(`${PATHS.app.myFoods.recipes}${mealTypeQueryParams}`)
  }

  const onViewIngredientDetails = (ingredient: IEdamaIngredientOption) => {
    setSection(CreateMyFoodFormSection.IngredientDetails)
    setIngredientDetails(ingredient)
  }

  const [isPortionValid, setIsPortionValid] = useState(true)

  const handlePortionChange = (value: number) => {
    onPortionChange(value)
    setIsPortionValid(true)
  }

  const shouldShowHeader =
    !isHideHeader && section !== CreateMyFoodFormSection.IngredientDetails

  const isFormEmpty = myFood!.ingredients.length === 0 || !myFood!.name

  const isFormInvalid = isFormEmpty || processImageLoading || !isPortionValid

  return (
    <CreateFoodWrapper
      $background={
        section === CreateMyFoodFormSection.Form ? "#f5f7f7" : "#fff"
      }
    >
      {shouldShowHeader && (
        <CreateFoodHeader
          section={section}
          isFormEmpty={isFormEmpty}
          setSection={setSection}
          setIsPopConfirmDiscardOpen={setIsPopConfirmDiscardOpen}
          disabled={createFoodLoading}
          // mealType={mealType}
        />
      )}

      {section === CreateMyFoodFormSection.Form && (
        <Fragment>
          <CreateMyFoodForm
            showSearchIngredients={showSearchIngredients}
            onViewIngredientDetails={onViewIngredientDetails}
            {...createMyFoodFormProps}
            myFood={myFood!}
            onPortionChange={handlePortionChange}
            setIsPortionValid={setIsPortionValid}
          />
          <AppStyleCtaBox>
            <Button
              disabled={isFormInvalid}
              loading={createFoodLoading}
              onClick={onSubmitCreateFood}
              style={{ width: "100%" }}
              $type="primary"
            >
              {" "}
              Save 
            </Button>
          </AppStyleCtaBox>
        </Fragment>
      )}

      {section === CreateMyFoodFormSection.IngredientDetails &&
        ingredientDetails && (
          <EdamaFoodDetails
            isIngredient
            food={ingredientDetails}
            onBack={() => {
              setSection(CreateMyFoodFormSection.Form)
            }}
            onEdit={(ingredient) => {
              onUpdateIngredient(ingredient)
            }}
            onDelete={() => {
              onRemoveIngredient(ingredientDetails.uuid)
              setSection(CreateMyFoodFormSection.Form)
            }}
            mode="edit"
          />
        )}

      {section === CreateMyFoodFormSection.SearchIngredients && (
        <SearchIngredients
          setIsHideHeader={setIsHideHeader}
          onAddIngredient={onAddIngredient}
        />
      )}

      <PopConfirm
        isOpen={isPopConfirmDiscardOpen}
        title="Unsaved changes?"
        cancelText="Discard"
        confirmText="Stay"
        onCancel={backToRecipes}
        onConfirm={() => {
          setIsPopConfirmDiscardOpen(false)
        }}
        loading={false}
      >
        <div style={{ textAlign: "center" }}>
          You haven't saved your meal yet. Are you sure you want to leave?
        </div>
      </PopConfirm>
    </CreateFoodWrapper>
  )
}

export default CreateMyFood
